import * as d3 from 'd3';
import React from 'react';
import { useLocation } from 'react-router-dom';
import { XAxis, YAxis } from 'react-stockcharts/lib/axes';
import { MouseCoordinateY } from 'react-stockcharts/lib/coordinates';
import { barChartTickFormat } from 'shared/util/utility';

import { useTranslation } from 'react-i18next';
import { mouseEdgeAppearance } from 'shared/constants/constants';
import { IChartDesignConfig } from '../interface/futureTable';

interface IProps {
	chartType: string;
	span: string;
	data: any;
	axisAt: string;
	chartDesignConfig: IChartDesignConfig;
	xTicks: number;
	yTicks: number;
	tickFormat: (d: number) => void;
	isCompareWithSelfEnabled: boolean;
	yAxisFontSize?: number;
	xAxisFontSize?: number;
}

const ChartAxis: React.FC<IProps> = (props) => {
	const { chartType, span, data, axisAt, chartDesignConfig, xTicks, yTicks, isCompareWithSelfEnabled, tickFormat, yAxisFontSize, xAxisFontSize } =
		props;
	const location = useLocation();
	const isIframe = location.pathname.includes('iframe');
	return (
		<>
			<YAxis
				axisAt={axisAt}
				orient={axisAt}
				ticks={yTicks}
				tickFormat={tickFormat}
				zoomEnabled={!isIframe}
				fontSize={isIframe ? 8 : yAxisFontSize !== undefined ? yAxisFontSize : chartDesignConfig?.fontSize}
			/>
			<ReturnXAxis
				chartType={chartType}
				isCompareWithSelfEnabled={isCompareWithSelfEnabled}
				span={span}
				xTicks={xTicks}
				data={data}
				chartDesignConfig={chartDesignConfig}
				xAxisFontSize={xAxisFontSize}
			/>
			{!isIframe && (
				<g className={`${axisAt === 'right' ? 'right' : 'left'}`}>
					<MouseCoordinateY
						at={axisAt}
						orient={axisAt}
						fontSize={yAxisFontSize !== undefined ? yAxisFontSize : chartDesignConfig?.fontSize}
						{...mouseEdgeAppearance}
						displayFormat={tickFormat}
						rectWidth={chartDesignConfig?.rectWidth}
						rectHeight={chartDesignConfig?.rectHeight}
					/>
				</g>
			)}
		</>
	);
};

export default ChartAxis;

export interface IChartXaxisProps {
	span: string;
	chartType: string;
	isCompareWithSelfEnabled?: boolean;
	xTicks: number;
	data: any;
	chartDesignConfig?: IChartDesignConfig;
	xAxisFontSize?: number;
}

const ReturnXAxis: React.FC<IChartXaxisProps> = ({ span, chartType, isCompareWithSelfEnabled, xTicks, data, chartDesignConfig, xAxisFontSize }) => {
	const location = useLocation();
	const isIframe = location.pathname.includes('iframe');
	const { t } = useTranslation();
	if (chartType === 'bar' && (span === '1y' || span === '1w')) {
		return (
			<XAxis
				axisAt='bottom'
				orient='bottom'
				ticks={xTicks}
				fontSize={isIframe ? 8 : xAxisFontSize !== undefined ? xAxisFontSize : chartDesignConfig?.fontSize}
				zoomEnabled={false}
				tickFormat={(d: number) => barChartTickFormat(d, data, span, t)}
			/>
		);
	} else if (isCompareWithSelfEnabled) {
		return (
			<XAxis
				axisAt='bottom'
				orient='bottom'
				ticks={xTicks}
				zoomEnabled={!isIframe}
				tickFormat={(d: number) => {
					const date = new Date(data[d].date);
					const formattedDate = d3.timeFormat('%d %b')(date);
					return formattedDate;
				}}
				fontSize={isIframe ? 8 : xAxisFontSize !== undefined ? xAxisFontSize : chartDesignConfig?.fontSize}
			/>
		);
	} else {
		return (
			<XAxis
				axisAt='bottom'
				orient='bottom'
				ticks={xTicks}
				zoomEnabled={!isIframe}
				fontSize={isIframe ? 8 : xAxisFontSize !== undefined ? xAxisFontSize : chartDesignConfig?.fontSize}
			/>
		);
	}
};
